import { type TFunction } from 'i18next';
import { Trans } from 'react-i18next';
import { twMerge } from 'tailwind-merge';

import { type OrganizationBilling, PlanStatus } from '../entity/api/organization-billing';
import { i18n, tNamespace } from '../services/translation';
import { getOrgCredit } from '../shared/credits';
import { formatDateToBasedOnLanguage } from '../shared/date';
import { Feature, isFeatureAvailable } from '../shared/feature';
import { Button } from './buttons/button';

const t = tNamespace('components.credit-usage');

export type CreditUsageProps = {
  organizationBilling: OrganizationBilling;
  categoryLabel?: string;
  onBuyCredits: () => void;
  className?: string;
};

function getProgressBarColor(percentage: number, isCancelled: boolean): string {
  if (isCancelled) {
    return 'bg-branding-accent-600';
  }
  if (percentage >= 100) {
    return 'bg-red-500';
  }
  if (percentage >= 90) {
    return 'bg-orange-400';
  }
  return 'bg-branding-accent-600';
}

function getStatusTextColor(isCancelled: boolean): string {
  if (isCancelled) {
    return 'text-red-500';
  }
  return 'text-gray';
}

export function CreditUsage(props: CreditUsageProps) {
  const { organizationBilling, categoryLabel, onBuyCredits, className } = props;

  const { total, used, usedPercentage, remainingPercentage } = getOrgCredit(organizationBilling);

  const extraCreditCount = organizationBilling?.plan?.extraCreditCount ?? 0;
  const renewalDate = organizationBilling?.currentPlanEndDate ?? 0;
  const restrictionDate = organizationBilling?.currentPlanEndDate ?? 0;

  const progressBarColor = getProgressBarColor(
    usedPercentage,
    organizationBilling?.currentPlanStatus == PlanStatus.Cancelling
  );
  const statusTextColor = getStatusTextColor(
    organizationBilling?.currentPlanStatus == PlanStatus.Cancelling
  );

  const backgroundColor =
    usedPercentage >= 100 && organizationBilling?.currentPlanStatus !== PlanStatus.Cancelling
      ? 'bg-red-50'
      : 'bg-white';

  return (
    <div
      className={twMerge(
        'flex flex-col gap-6 rounded-lg border border-branding-gray p-6',
        backgroundColor,
        className
      )}
    >
      {/* Header Section */}
      <div className="flex flex-col gap-1">
        <h3 className="font-body-4">{t('title')}</h3>
        <p className="font-body-2-r text-gray">{t('subtitle')}</p>
      </div>

      <div className="flex w-full items-center justify-between gap-4">
        {/* Usage Stats and Progress */}
        <div className="flex w-full flex-col gap-2">
          {/* Category Label and Current/Total */}
          <div className="flex items-center justify-between">
            <p className="font-body-2-m text-dark-gray">{categoryLabel}</p>
            <div className="flex items-center gap-2">
              <p className="font-body-2-m text-gray">
                {used} / {total}
              </p>
              {extraCreditCount > 0 ? (
                <p className="font-body-2-m text-green-500">(+ {extraCreditCount})</p>
              ) : null}
            </div>
          </div>

          {/* Progress Bar */}
          <div className="h-2 w-full rounded-full bg-branding-accent-200">
            <div
              className={twMerge(
                'h-full rounded-full transition-all duration-300',
                progressBarColor
              )}
              style={{ width: `${Math.min(usedPercentage, 100)}%` }}
            />
          </div>

          {/* Status Text */}
          <p className={twMerge('font-body-2-m', statusTextColor)}>
            {organizationBilling?.currentPlanStatus == PlanStatus.Cancelling ? (
              <>
                {t('percentRemaining', {
                  percentage: remainingPercentage,
                  date: formatDateToBasedOnLanguage(new Date(restrictionDate ?? 0), i18n.language),
                })}
              </>
            ) : (
              <>
                {t('percentUsed', { percentage: usedPercentage })} ·{' '}
                {t('renews', {
                  date: formatDateToBasedOnLanguage(new Date(renewalDate ?? 0), i18n.language),
                })}
              </>
            )}
          </p>
          {extraCreditCount > 0 ? (
            <p className="font-body-2-m text-gray">
              <Trans
                t={t as unknown as TFunction}
                i18nKey="extraCreditsCount"
                values={{ count: extraCreditCount }}
                components={[
                  <span className="font-body-2-m text-green-500" key="count" />,
                  <span className="font-body-2-m text-light-gray" key="neverExpire" />,
                ]}
              />
            </p>
          ) : null}
        </div>

        {/* Buy Credits Button */}
        {isFeatureAvailable(Feature.CREDIT_PAYMENT_AVAILABLE) ? (
          <Button variant="accent" size="sm" onClick={onBuyCredits} className="flex-shrink-0">
            {t('buyCredits')}
          </Button>
        ) : null}
      </div>
    </div>
  );
}
